import image_logo from "../../assets/images/logo.svg";
import { PROVIDER, controllerLine } from "../../config/legalEntities";

function CookiePolicy() {
  const appName = process.env.REACT_APP_NAME || "TruHail";
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 py-10">
      <div className="mb-8 h-[80px] overflow-hidden rounded-2xl">
        <img
          src={image_logo}
          alt={`Logo ${appName}`}
          height={80}
          className="h-full w-full object-cover"
        />
      </div>

      <div className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow-md">
        <h1 className="mb-4 text-2xl font-bold">Política de Cookies</h1>
        <p className="mb-6 text-sm text-gray-500">
          Última atualização: 1 de julho de 2026
        </p>
        <p className="mb-8 text-lg">
          Esta Política de Cookies explica quais cookies e tecnologias
          semelhantes são utilizados na plataforma {appName}, para que servem e
          como você pode gerenciá-los. Ela complementa a nossa Política de
          Privacidade e observa as Leis de Proteção de Dados Aplicáveis
          (incluindo a LGPD — Lei nº 13.709/2018).
        </p>

        <div className="mb-6 rounded-lg bg-blue-50 p-4">
          <h3 className="mb-2 font-semibold text-blue-800">
            O que são cookies e tecnologias semelhantes
          </h3>
          <p className="text-sm text-blue-700">
            Cookies são pequenos arquivos armazenados no seu dispositivo.
            Também utilizamos tecnologias equivalentes, como{" "}
            <strong>localStorage</strong> e tokens de sessão, que cumprem
            função semelhante à dos cookies para o funcionamento da plataforma.
            Neste documento, o termo "cookies" abrange todas essas tecnologias.
          </p>
        </div>

        <div className="space-y-8">
          {/* 1. O que usamos */}
          <div>
            <h2 className="mb-3 text-xl font-semibold">
              1. O que usamos — tecnologias essenciais
            </h2>
            <p className="mb-2">
              Utilizamos apenas o estritamente necessário para que a plataforma
              funcione e para respeitar as suas escolhas:
            </p>
            <ul className="list-inside list-disc space-y-1">
              <li>
                <strong>Consentimento (localStorage):</strong> registramos
                localmente, no seu navegador, se você aceitou ou recusou o uso
                de cookies, para não exibir o banner repetidamente e respeitar a
                sua decisão.
              </li>
              <li>
                <strong>Sessão / token de autenticação:</strong> mantêm você
                conectado com segurança durante o uso do painel, evitando novo
                login a cada página. Sem eles, a autenticação não funciona.
              </li>
            </ul>
            <p className="mt-3 text-sm text-gray-600">
              Essas tecnologias são consideradas essenciais e indispensáveis à
              prestação do serviço; sem elas, funcionalidades básicas como login
              e a memória da sua escolha de consentimento deixam de operar.
            </p>
          </div>

          {/* 2. Cookies / serviços de terceiros */}
          <div>
            <h2 className="mb-3 text-xl font-semibold">
              2. Cookies e serviços de terceiros
            </h2>
            <p className="mb-2 text-sm text-gray-600">
              Ao exibir mapas e ao servir a aplicação, recorremos a provedores
              externos que, por natureza técnica, podem registrar dados como o
              seu endereço IP e a área do mapa visualizada. Cada provedor possui
              sua própria política:
            </p>
            <ul className="list-inside list-disc space-y-2">
              <li>
                <strong>OpenStreetMap</strong> — mapas e geocodificação; pode
                registrar IP e a área do mapa consultada (
                <a
                  href="https://wiki.osmfoundation.org/wiki/Privacy_Policy"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Política da OpenStreetMap Foundation
                </a>
                )
              </li>
              <li>
                <strong>CARTO</strong> — camadas e ladrilhos de mapa; pode
                registrar IP e a área visualizada (
                <a
                  href="https://carto.com/privacy"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Política da CARTO
                </a>
                )
              </li>
              <li>
                <strong>Firebase / Google</strong> — hospedagem e entrega da
                aplicação; pode registrar IP e dados técnicos de acesso (
                <a
                  href="https://policies.google.com/technologies/cookies"
                  className="text-blue-600 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Como o Google usa cookies
                </a>
                )
              </li>
            </ul>
            <p className="mt-3 text-sm text-gray-500">
              As URLs e versões das políticas de terceiros podem mudar; confira
              na data de acesso.
            </p>
          </div>

          {/* 3. Como gerenciar */}
          <div>
            <h2 className="mb-3 text-xl font-semibold">
              3. Como gerenciar cookies
            </h2>
            <ul className="list-inside list-disc space-y-1">
              <li>
                <strong>Banner de consentimento:</strong> você pode{" "}
                <strong>aceitar</strong> ou <strong>recusar</strong> os cookies
                não essenciais diretamente no banner exibido ao acessar a
                plataforma.
              </li>
              <li>
                <strong>Limpar no navegador:</strong> você pode, a qualquer
                momento, apagar cookies, o localStorage e os dados de sites nas
                configurações do seu navegador. Ao limpar o registro de
                consentimento, o banner será exibido novamente na próxima
                visita.
              </li>
              <li>
                <strong>Bloqueio:</strong> a maioria dos navegadores permite
                bloquear cookies; note que bloquear os essenciais pode impedir o
                login e o funcionamento adequado do painel.
              </li>
            </ul>
          </div>

          {/* 4. Contato do DPO */}
          <div>
            <h2 className="mb-3 text-xl font-semibold">
              4. Encarregado de Dados (DPO) e Contato
            </h2>
            <p>
              Para dúvidas sobre esta Política de Cookies ou para exercer seus
              direitos, contate o Encarregado de Proteção de Dados (DPO):{" "}
              <a
                href={`mailto:${PROVIDER.dpoEmail}`}
                className="text-blue-600 underline"
              >
                {PROVIDER.dpoEmail}
              </a>
              .
            </p>
            <p className="mt-2 text-sm text-gray-600">
              A Controladora dos dados é {controllerLine}.
            </p>
          </div>

          {/* Links relacionados */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="mb-2 font-semibold">Documentos Relacionados</h3>
            <ul className="list-inside list-disc space-y-1">
              <li>
                <a href="/privacy-policy-rider" className="text-blue-600 underline">
                  Política de Privacidade — Passageiros
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;
